import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Eye } from 'lucide-react';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  city?: string;
  full_name: string;
}

interface QuestionnaireRequest {
  id: number;
  specialty_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  physiotherapist: Physiotherapist;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  requests: QuestionnaireRequest[];
  pagination: Pagination;
}

const getStatusBadge = (status: string) => {
  const statusConfig = {
    pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800 border-green-300' },
    rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800 border-red-300' }
  };
  
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  
  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
};

export default function QuestionnaireRequestsIndex({ requests, pagination }: Props) {
  return (
    <AdminLayout title="Demandes de questionnaires">
      <Head title="Questionnaires - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Demandes de questionnaires</h1>
            <p className="text-gray-600">
              {pagination.total_count} demande{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
        </div>

        {/* Requests List */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des demandes</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length > 0 ? (
              <div className="space-y-4">
                {requests.map((request) => (
                  <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {request.physiotherapist.full_name}
                          </h3>
                          {getStatusBadge(request.status)}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><strong>Spécialité :</strong> {request.specialty_name}</p>
                          <p><strong>Demandé le :</strong> {new Date(request.created_at).toLocaleDateString('fr-FR')}</p>
                          {request.physiotherapist.city && (
                            <p><strong>Ville :</strong> {request.physiotherapist.city}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link href={`/admin/questionnaire_requests/${request.id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            Voir
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Aucune demande de questionnaire</p>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/admin/questionnaire_requests?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/admin/questionnaire_requests?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

